
import { useState, useEffect } from "react";
import WorkerHeader from '../../worker-header/components/index';
import WorkerFooter from "../../worker-footer/components/index";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addSnapshot } from "../slice/workerSnapshotSlice";
import {
    Stepper,
    Step,
    StepLabel,
    Button,
    TextField,
    FormControl,
    FormLabel,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    Paper,
    Grid,
    Typography,
    Divider,
    Box,
    Checkbox
} from "@mui/material";

const states = [
    { id: 1, code: 'AL', name: 'Alabama' },
    { id: 2, code: 'AK', name: 'Alaska' },
    { id: 3, code: 'AZ', name: 'Arizona' },
    { id: 4, code: 'AR', name: 'Arkansas' },
    { id: 5, code: 'CA', name: 'California' },
    { id: 6, code: 'CO', name: 'Colorado' },
    { id: 7, code: 'CT', name: 'Connecticut' },
    { id: 8, code: 'DE', name: 'Delaware' },
    { id: 9, code: 'FL', name: 'Florida' },
    { id: 10, code: 'GA', name: 'Georgia' },
    { id: 11, code: 'HI', name: 'Hawaii' },
    { id: 12, code: 'ID', name: 'Idaho' },
    { id: 13, code: 'IL', name: 'Illinois' },
    { id: 14, code: 'IN', name: 'Indiana' },
    { id: 15, code: 'IA', name: 'Iowa' },
    { id: 16, code: 'KS', name: 'Kansas' },
    { id: 17, code: 'KY', name: 'Kentucky' },
    { id: 18, code: 'LA', name: 'Louisiana' },
    { id: 19, code: 'ME', name: 'Maine' },
    { id: 20, code: 'MD', name: 'Maryland' },
    { id: 21, code: 'MA', name: 'Massachusetts' },
    { id: 22, code: 'MI', name: 'Michigan' },
    { id: 23, code: 'MN', name: 'Minnesota' },
    { id: 24, code: 'MS', name: 'Mississippi' },
    { id: 25, code: 'MO', name: 'Missouri' },
    { id: 26, code: 'MT', name: 'Montana' },
    { id: 27, code: 'NE', name: 'Nebraska' },
    { id: 28, code: 'NV', name: 'Nevada' },
    { id: 29, code: 'NH', name: 'New Hampshire' },
    { id: 30, code: 'NJ', name: 'New Jersey' },
    { id: 31, code: 'NM', name: 'New Mexico' },
    { id: 32, code: 'NY', name: 'New York' },
    { id: 33, code: 'NC', name: 'North Carolina' },
    { id: 34, code: 'ND', name: 'North Dakota' },
    { id: 35, code: 'OH', name: 'Ohio' },
    { id: 36, code: 'OK', name: 'Oklahoma' },
    { id: 37, code: 'OR', name: 'Oregon' },
    { id: 38, code: 'PA', name: 'Pennsylvania' },
    { id: 39, code: 'RI', name: 'Rhode Island' },
    { id: 40, code: 'SC', name: 'South Carolina' },
    { id: 41, code: 'SD', name: 'South Dakota' },
    { id: 42, code: 'TN', name: 'Tennessee' },
    { id: 43, code: 'TX', name: 'Texas' },
    { id: 44, code: 'UT', name: 'Utah' },
    { id: 45, code: 'VT', name: 'Vermont' },
    { id: 46, code: 'VA', name: 'Virginia' },
    { id: 47, code: 'WA', name: 'Washington' },
    { id: 48, code: 'WV', name: 'West Virginia' },
    { id: 49, code: 'WI', name: 'Wisconsin' },
    { id: 50, code: 'WY', name: 'Wyoming' },
];

const roleMap = {
    "Director / Administrator": 1,
    "Lead Teachers": 2,
    "Assistant Teachers / Aides": 3,
    "Substitutes / Floaters": 4,
    "Support Staff (non-classroom)": 5
};

const workforceMap = {
    "Staff turnover": 1,
    "Coverage gaps / call-outs": 2,
    "Difficulty onboarding new hires": 3,
    "None of the above": 4
};

const WorkerSnapshot = () => {

    const [activeStep, setActiveStep] = useState(0);
    const dispatch = useDispatch();
    const steps = [
        "BASIC CONTEXT",
        "STAFF ROLES",
        "KEY COMPLIANCE DATES",
        "DOCUMENTATION CONFIDENCE",
        "WORKFORCE STABILITY SIGNAL",
        "SNAPSHOT DELIVERY",
    ];
    const { loading } = useSelector((state) => state.workerSnapshot);

    const [formData, setFormData] = useState({
        step0: { stateOfOperation: "", providerType: "", numLocations: "", totalStaff: "" },
        step1: { roles: [] },
        step2: { backgroundCheck: "", cpr: "", training: "", licensing: "" },
        step3: { documentationConfidence: "" },
        step4: { workforceChallenges: [] },
        step5: { email: "", centerName: "", zipCode: "", acknowledgment: false },
    });

    const [timeLeft, setTimeLeft] = useState(0); // seconds
    const stepTime = [60, 45, 120, 30, 30]; // in seconds for steps 0-5

    useEffect(() => {
        setTimeLeft(stepTime[activeStep]); // reset time for new step
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleAutoSelect(activeStep);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer); // cleanup on step change
    }, [activeStep]);


    const handleNext = () => {
        if (!validateStep(activeStep)) return;
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };
    const handleAutoSelect = (step) => {
        switch (step) {
            case 0:
                setFormData(prev => ({
                    ...prev,
                    step0: {
                        // stateOfOperation: prev.step0.stateOfOperation || "WY",
                        // providerType: prev.step0.providerType || "3",
                        numLocations: prev.step0.numLocations || "3",
                        totalStaff: prev.step0.totalStaff || "4"
                    }
                }));
                break;

            case 1:
                setFormData(prev => ({
                    ...prev,
                    step1: {
                        roles: prev.step1.roles.length ? prev.step1.roles : "5"
                    }
                }));
                break;

            case 2:
                setFormData(prev => ({
                    ...prev,
                    step2: {
                        backgroundCheck: prev.step2.backgroundCheck || "4",
                        cpr: prev.step2.cpr || "4",
                        training: prev.step2.training || "3",
                        licensing: prev.step2.licensing || "4"
                    }
                }));
                break;

            case 3:
                setFormData(prev => ({
                    ...prev,
                    step3: {
                        documentationConfidence: prev.step3.documentationConfidence || "4"
                    }
                }));
                break;

            case 4:
                setFormData(prev => ({
                    ...prev,
                    step4: {
                        workforceChallenges: prev.step4.workforceChallenges.length
                            ? prev.step4.workforceChallenges
                            : 4
                    }
                }));
                break;

            case 5:
                setFormData(prev => ({
                    ...prev,
                    step5: {
                        email: prev.step5.email || "",
                        centerName: prev.step5.centerName || "",
                        zipCode: prev.step5.zipCode || "",
                        acknowledgment: prev.step5.acknowledgment || true
                    }
                }));
                break;

            default:
                break;
        }
        // if (step < steps.length - 1) {
        //     setActiveStep(prev => prev + 1);
        // }
    };

    const validateStep = (step) => {
        switch (step) {
            case 0:
                if (!formData.step0.providerType) {
                    toast.error("Please select provider type.");
                    return false;
                }
                if (!formData.step0.stateOfOperation) {
                    toast.error("Please select a state.");
                    return false;
                }
                return true;
            default:
                return true;

        }
    };

    const handleSubmit = () => {
        if (!formData.step5.email) {
            toast.error("Please enter Email.");
            return;
        }

        if (!formData.step5.acknowledgment) {
            toast.error("Please check acknowledgment.");
            return;
        }

        const finalData = {
            step0: {
                stateOfOperation: formData.step0.stateOfOperation,
                providerType: formData.step0.providerType,
                numLocations: formData.step0.numLocations || "3",
                totalStaff: formData.step0.totalStaff || "4",
            },
            step1: {
                roles: formData.step1.roles.length
                    ? formData.step1.roles
                    : ["5"],
            },
            step2: {
                backgroundCheck: formData.step2.backgroundCheck || "4",
                cpr: formData.step2.cpr || "4",
                training: formData.step2.training || "3",
                licensing: formData.step2.licensing || "4",
            },
            step3: {
                documentationConfidence:
                    formData.step3.documentationConfidence || "4",
            },
            step4: {
                workforceChallenges:
                    formData.step4.workforceChallenges.length
                        ? formData.step4.workforceChallenges
                        : ["4"],
            },
            step5: formData.step5,
        };

        console.log("Final Payload:", finalData);

        const answers = [];

        // STEP 0
        Object.entries(finalData.step0).forEach(([key, value]) => {
            answers.push({
                que_name: key,
                answer: value,
            });
        });

        // STEP 1 (roles multiple)
        finalData.step1.roles.forEach((role) => {
            answers.push({
                que_name: "roles",
                answer: role,
            });
        });

        // STEP 2
        Object.entries(finalData.step2).forEach(([key, value]) => {
            answers.push({
                que_name: key,
                answer: value,
            });
        });

        // STEP 3
        answers.push({
            que_name: "documentationConfidence",
            answer: finalData.step3.documentationConfidence,
        });

        // STEP 4 (multiple)
        finalData.step4.workforceChallenges.forEach((val) => {
            answers.push({
                que_name: "workforceChallenges",
                answer: val,
            });
        });

        const payload = {
            acknowledgment: finalData.step5.acknowledgment,
            center_name: finalData.step5.centerName,
            email: finalData.step5.email,
            zip_code: finalData.step5.zipCode,
            answers
        };

        console.log("Final API Payload:", payload);

        dispatch(addSnapshot(payload))
            .unwrap()
            .then(() => {
                toast.success("Snapshot stored successfull");
                // navigate("/panel/my-job");
            })
            .catch(() => toast.error("Failed to snapshot."));
    };


    return (
        <>
            <WorkerHeader />
            <div className="container">
                <div className="col-md-8 col-xl-12 mt-4 card shadow-sm m-2 p-4">
                    <div className="ps-2 ps-lg-4">
                        <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
                            <h4 className="font-weight-bold text-primary font-size-40 line-hight-2 montserrat mb-0">
                                Snapshot
                            </h4>

                        </div>
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <main className="container py-2">
                            <div className="m-2">
                                {activeStep === 0 && (
                                    <>
                                        {/* Section Header */}
                                        <Box sx={{display: "flex" , justifyContent:"space-between" , }}>
                                            <Box >
                                                <Typography variant="h6" fontWeight="bold" className="mb-2">
                                                    BASIC CONTEXT
                                                </Typography>
                                                {/* <Typography variant="body2" color="text.secondary">
                                                    Purpose: Determine which rules apply
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Takes less than 60 seconds
                                                </Typography> */}
                                            </Box>
                                            <Box >
                                                <Typography variant="h6" color="text.secondary">
                                                    Time left: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {/* <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                                            <Typography variant="h6" color="text.secondary">
                                                Time left: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
                                            </Typography>
                                        </Box> */}


                                        <Divider sx={{ mb: 3 }} />

                                        <Box sx={{ width: '100%' }}>
                                            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>

                                                <Grid size={12}>
                                                    <Paper elevation={2} sx={{ p: 3 }}>
                                                        <Typography fontWeight={600}>
                                                            Provider Type
                                                        </Typography>
                                                        <FormControl fullWidth margin="normal" variant="outlined">
                                                            <RadioGroup
                                                                value={formData.step0.providerType}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        step0: { ...prev.step0, providerType: e.target.value }
                                                                    }))
                                                                }
                                                            >
                                                                <FormControlLabel
                                                                    value="1"
                                                                    control={<Radio />}
                                                                    label="Licensed Childcare Center"
                                                                />
                                                                <FormControlLabel
                                                                    value="2"
                                                                    control={<Radio />}
                                                                    label="Family / Home-Based Childcare"
                                                                />
                                                                <FormControlLabel
                                                                    value="3"
                                                                    control={<Radio />}
                                                                    label="License-Exempt / Informal Care"
                                                                />
                                                            </RadioGroup>
                                                        </FormControl>
                                                    </Paper>
                                                </Grid>
                                                <Grid size={12} sx={{ mt: 3 }}>
                                                    <Paper elevation={2} sx={{ p: 3 }}>
                                                        <Typography fontWeight={600}>
                                                            State of Operation
                                                        </Typography>
                                                        <FormControl fullWidth margin="normal" variant="outlined">
                                                            {/* <InputLabel>State of Operation</InputLabel> */}
                                                            <Select
                                                                // label="State of Operation"
                                                                value={formData.step0.stateOfOperation}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        step0: { ...prev.step0, stateOfOperation: e.target.value }
                                                                    }))
                                                                }
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        style: {
                                                                            maxHeight: 300, // dropdown max height
                                                                        },
                                                                    },
                                                                }}
                                                            >
                                                                {states.map((state) => (
                                                                    <MenuItem key={state.id} value={state.code}>
                                                                        {state.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Paper>
                                                </Grid>
                                                <Grid size={6} sx={{ mt: 3 }}>
                                                    <Paper elevation={2} sx={{ p: 3 }}>
                                                        <Typography fontWeight={600}>
                                                            Number of Locations
                                                        </Typography>
                                                        <FormControl fullWidth margin="normal" variant="outlined">
                                                            {/* <InputLabel>Number of Locations</InputLabel> */}
                                                            <Select
                                                                // label="Number of Locations"
                                                                value={formData.step0.numLocations}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        step0: { ...prev.step0, numLocations: e.target.value }
                                                                    }))
                                                                }
                                                            >
                                                                <MenuItem value="1">1</MenuItem>
                                                                <MenuItem value="2">2–5</MenuItem>
                                                                <MenuItem value="3">6+</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Paper>
                                                </Grid>
                                                <Grid size={6} sx={{ mt: 3 }}>
                                                    <Paper elevation={2} sx={{ p: 3 }}>
                                                        <Typography fontWeight={600}>
                                                            Approximate Total Staff Count
                                                        </Typography>
                                                        <FormControl fullWidth margin="normal" variant="outlined">
                                                            {/* <InputLabel>Approximate Total Staff Count</InputLabel> */}
                                                            <Select
                                                                // label="Approximate Total Staff Count"
                                                                value={formData.step0.totalStaff}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        step0: { ...prev.step0, totalStaff: e.target.value }
                                                                    }))
                                                                }
                                                            >
                                                                <MenuItem value="1">1–5</MenuItem>
                                                                <MenuItem value="2">6–15</MenuItem>
                                                                <MenuItem value="3">16–30</MenuItem>
                                                                <MenuItem value="4">30+</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Paper>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </>
                                )}

                                {activeStep === 1 && (
                                    <>
                                        {/* Section Header */}
                                      <Box sx={{display: "flex" , justifyContent:"space-between"}}> 
                                        <Box >
                                            <Typography variant="h6" fontWeight="bold" className="mb-2">
                                                STAFF ROLES
                                            </Typography>
                                            {/* <Typography variant="body2" color="text.secondary">
                                                Purpose: Apply role-based requirements
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Takes less than 45 seconds
                                            </Typography> */}
                                        </Box>

                                        <Box >
                                            <Typography variant="h6" color="text.secondary">
                                                Time left: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
                                            </Typography>
                                        </Box>
                                    </Box> 

                                        <Divider sx={{ mb: 3}} />

                                        {/* Question Prompt */}
                                        <FormControl component="fieldset" fullWidth>
                                            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                                                Which roles do you currently have on staff?
                                            </FormLabel>

                                            {Object.entries(roleMap).map(([role, value]) => (
                                                <FormControlLabel
                                                    key={role}
                                                    control={
                                                        <Checkbox
                                                            checked={formData.step1.roles.includes(value)}
                                                            onChange={() => {
                                                                setFormData(prev => {
                                                                    const roles = prev.step1.roles.includes(value)
                                                                        ? prev.step1.roles.filter(r => r !== value) // remove if already selected
                                                                        : [...prev.step1.roles, value]; // add numeric value
                                                                    return { ...prev, step1: { roles } };
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={role}
                                                />
                                            ))}
                                        </FormControl>
                                    </>
                                )}

                                {activeStep === 2 && (
                                    <>
                                        {/* Section Header */}
                                        <Box sx={{display: "flex" , justifyContent:"space-between"}}>
                                        <Box >
                                            <Typography variant="h6" fontWeight="bold" className="mb-2">
                                                KEY COMPLIANCE DATES
                                            </Typography>
                                            {/* <Typography variant="body2" color="text.secondary">
                                                Purpose: Generate Green / Yellow / Red
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Takes 1–2 minutes
                                            </Typography> */}
                                        </Box>

                                        <Box >
                                            <Typography variant="h6" color="text.secondary">
                                                Time left: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
                                            </Typography>
                                        </Box>
                                        </Box>

                                        <Divider sx={{ mb: 3}} />

                                        <Box sx={{ width: '100%' }}>
                                            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>

                                                {/* 3.1 Background Checks */}
                                                <Grid size={6}>
                                                    <Paper elevation={2} sx={{ p: 3 }}>
                                                        <Typography fontWeight={600} mb={1}>
                                                            3.1 Background Checks
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" mb={2}>
                                                            For your active classroom staff, when were background checks last completed?
                                                        </Typography>

                                                        <FormControl fullWidth>
                                                            <InputLabel id="background-check-label">Select an option</InputLabel>
                                                            <Select
                                                                labelId="background-check-label"
                                                                label="Select an option"
                                                                value={formData.step2.backgroundCheck}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        step2: { ...prev.step2, backgroundCheck: e.target.value }
                                                                    }))
                                                                }
                                                            >
                                                                <MenuItem value="1">
                                                                    Within the last 12 months
                                                                </MenuItem>
                                                                <MenuItem value="2">
                                                                    1–2 years ago
                                                                </MenuItem>
                                                                <MenuItem value="3">
                                                                    More than 2 years ago
                                                                </MenuItem>
                                                                <MenuItem value="4">
                                                                    Not sure
                                                                </MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Paper>
                                                </Grid>

                                                {/* 3.2 CPR / First Aid */}
                                                <Grid size={6}>
                                                    <Paper elevation={2} sx={{ p: 3 }}>
                                                        <Typography fontWeight={600} mb={1}>
                                                            3.2 CPR / First Aid
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" mb={2}>
                                                            When do CPR / First Aid certifications next expire for classroom staff?
                                                        </Typography>

                                                        <FormControl fullWidth>
                                                            <InputLabel id="cpr-label">Select an option</InputLabel>
                                                            <Select
                                                                labelId="cpr-label"
                                                                label="Select an option"
                                                                value={formData.step2.cpr}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        step2: { ...prev.step2, cpr: e.target.value }
                                                                    }))
                                                                }
                                                            >
                                                                <MenuItem value="1">
                                                                    Within the next 30 days
                                                                </MenuItem>
                                                                <MenuItem value="2">
                                                                    1–6 months
                                                                </MenuItem>
                                                                <MenuItem value="3">
                                                                    More than 6 months
                                                                </MenuItem>
                                                                <MenuItem value="4">
                                                                    Not sure
                                                                </MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Paper>
                                                </Grid>

                                                {/* 3.3 Required Training Cycle */}
                                                <Grid size={6} sx={{ mt: 3 }}>
                                                    <Paper elevation={2} sx={{ p: 3 }}>
                                                        <Typography fontWeight={600} mb={1}>
                                                            3.3 Required Training Cycle
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" mb={2}>
                                                            When was the most recent required annual training completed?
                                                        </Typography>

                                                        <FormControl fullWidth>
                                                            <InputLabel id="training-label">Select an option</InputLabel>
                                                            <Select
                                                                labelId="training-label"
                                                                label="Select an option"
                                                                value={formData.step2.training}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        step2: { ...prev.step2, training: e.target.value }
                                                                    }))
                                                                }
                                                            >
                                                                <MenuItem value="1">
                                                                    Within the last 12 months
                                                                </MenuItem>
                                                                <MenuItem value="2">
                                                                    More than 12 months ago
                                                                </MenuItem>
                                                                <MenuItem value="3">
                                                                    Not sure
                                                                </MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Paper>
                                                </Grid>

                                                {/* 3.4 Licensing / Inspection */}
                                                <Grid size={6} sx={{ mt: 3 }}>
                                                    <Paper elevation={2} sx={{ p: 3 }}>
                                                        <Typography fontWeight={600} mb={1}>
                                                            3.4 Licensing / Inspection
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" mb={2}>
                                                            When was your most recent licensing inspection?
                                                        </Typography>

                                                        <FormControl fullWidth>
                                                            <InputLabel id="inspection-label">Select an option</InputLabel>
                                                            <Select
                                                                labelId="inspection-label"
                                                                label="Select an option"
                                                                value={formData.step2.licensing}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        step2: { ...prev.step2, licensing: e.target.value }
                                                                    }))
                                                                }
                                                            >
                                                                <MenuItem value="1">
                                                                    Within the last year
                                                                </MenuItem>
                                                                <MenuItem value="2">
                                                                    1–2 years ago
                                                                </MenuItem>
                                                                <MenuItem value="3">
                                                                    More than 2 years ago
                                                                </MenuItem>
                                                                <MenuItem value="4">
                                                                    Not applicable / Not sure
                                                                </MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Paper>
                                                </Grid>

                                            </Grid>
                                        </Box>
                                    </>
                                )}

                                {activeStep === 3 && (
                                    <>
                                        {/* Section Header */}
                                        <Box sx={{display: "flex" , justifyContent:"space-between"}}>
                                        <Box >
                                            <Typography variant="h6" fontWeight="bold" className="mb-2">
                                                DOCUMENTATION CONFIDENCE
                                            </Typography>
                                            {/* <Typography variant="body2" color="text.secondary">
                                                Purpose: Detect admin strain
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Takes less than 30 seconds
                                            </Typography> */}
                                        </Box>

                                        <Box >
                                            <Typography variant="h6" color="text.secondary">
                                                Time left: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
                                            </Typography>
                                        </Box>
                                    </Box>

                                        <Divider sx={{ mb: 3 }} />

                                        <FormControl component="fieldset" fullWidth>
                                            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                                                How confident are you that required staff documents are current and accessible?
                                            </FormLabel>
                                            <RadioGroup
                                                value={formData.step3.documentationConfidence}
                                                onChange={(e) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        step3: { ...prev.step3, documentationConfidence: e.target.value }
                                                    }))
                                                }
                                            >
                                                <FormControlLabel
                                                    value="1"
                                                    control={<Radio />}
                                                    label="Very confident"
                                                />
                                                <FormControlLabel
                                                    value="2"
                                                    control={<Radio />}
                                                    label="Somewhat confident"
                                                />
                                                <FormControlLabel
                                                    value="3"
                                                    control={<Radio />}
                                                    label="Not confident"
                                                />
                                                <FormControlLabel
                                                    value="4"
                                                    control={<Radio />}
                                                    label="Not sure"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </>
                                )}

                                {activeStep === 4 && (
                                    <>
                                        {/* Section Header */}
                                        <Box sx={{display: "flex" , justifyContent:"space-between" }}>
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold" className="mb-2">
                                                WORKFORCE STABILITY SIGNAL
                                            </Typography>
                                            {/* <Typography variant="body2" color="text.secondary">
                                                Purpose: Seamlessly connect to onboarding & benefits
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Takes less than 30 seconds
                                            </Typography> */}
                                        </Box>

                                        <Box >
                                            <Typography variant="h6" color="text.secondary">
                                                Time left: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
                                            </Typography>
                                        </Box>
                                        </Box>

                                        <Divider sx={{ mb: 3 }} />

                                        {/* Question Prompt */}
                                        <FormControl component="fieldset" fullWidth>
                                            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                                                In the last 6 months, have you experienced staffing challenges that affected operations?
                                            </FormLabel>

                                            {Object.entries(workforceMap).map(([label, value]) => (
                                                <FormControlLabel
                                                    key={label}
                                                    control={
                                                        <Checkbox
                                                            checked={formData.step4.workforceChallenges.includes(value)}
                                                            onChange={() => {
                                                                setFormData(prev => {
                                                                    const workforceChallenges = prev.step4.workforceChallenges.includes(value)
                                                                        ? prev.step4.workforceChallenges.filter(r => r !== value)
                                                                        : [...prev.step4.workforceChallenges, value];
                                                                    return { ...prev, step4: { workforceChallenges } };
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={label}
                                                />
                                            ))}
                                        </FormControl>
                                    </>
                                )}

                                {activeStep === 5 && (
                                    <>
                                        {/* Section Header */}
                                        <Box >
                                            <Typography variant="h6" fontWeight="bold" className="mb-2">
                                                SNAPSHOT DELIVERY
                                            </Typography>
                                            {/* <Typography variant="body2" color="text.secondary">
                                                Purpose: Deliver your Snapshot results
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                No time limit
                                            </Typography> */}
                                        </Box>


                                        <Divider sx={{ mb: 4 }} />

                                        <Paper elevation={2} sx={{ p: 4 }}>
                                            <Grid container spacing={3}>

                                                {/* Email Address (Required) */}
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label="Email Address"
                                                        type="email"
                                                        required
                                                        value={formData.step5.email}
                                                        onChange={(e) =>
                                                            setFormData(prev => ({ ...prev, step5: { ...prev.step5, email: e.target.value } }))
                                                        }
                                                        fullWidth
                                                        helperText="We’ll send your Snapshot results to this email."
                                                    />
                                                </Grid>

                                                {/* Center Name (Optional) */}
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        label="Center Name (Optional)"
                                                        value={formData.step5.centerName}
                                                        onChange={(e) =>
                                                            setFormData(prev => ({ ...prev, step5: { ...prev.step5, centerName: e.target.value } }))
                                                        }
                                                        fullWidth
                                                    />
                                                </Grid>

                                                {/* ZIP Code (Optional) */}
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        label="ZIP Code (Optional)"
                                                        value={formData.step5.zipCode}
                                                        onChange={(e) =>
                                                            setFormData(prev => ({ ...prev, step5: { ...prev.step5, zipCode: e.target.value } }))
                                                        }
                                                        fullWidth
                                                    />
                                                </Grid>

                                                {/* Required Acknowledgment Checkbox */}
                                                <Grid item xs={12}>
                                                    <FormControl required>
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={formData.step5.acknowledgment}
                                                                onChange={(e) =>
                                                                    setFormData(prev => ({ ...prev, step5: { ...prev.step5, acknowledgment: e.target.checked } }))
                                                                }
                                                            />}
                                                            label={
                                                                <Typography variant="body2">
                                                                    I understand this Snapshot is based on information I provided
                                                                    and is not a compliance determination.
                                                                </Typography>
                                                            }
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </>
                                )}

                                <div className="mt-4 d-flex justify-content-between">
                                    <Button className="btn btn-light-header text-white" disabled={activeStep === 0} onClick={handleBack}>
                                        Back
                                    </Button>
                                    {activeStep === steps.length - 1 ? (
                                        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                                            {loading ? "Please wait..." : "Generate My Compliance Snapshot"}
                                        </Button>
                                    ) : (
                                        <Button variant="contained" onClick={handleNext}>
                                            Next
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </div>
            <WorkerFooter />
        </>
    )
}

export default WorkerSnapshot;